import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';
import { fmtTime, fmtDateTime, toDateTimeLocalInput, dateTimeLocalToUtcIso } from '../utils/datetimeFix';

/**
 * TimesheetView — reusable timesheet viewer for admins and staff.
 *
 * Capabilities:
 *  - Period filter chips: This week / Last week / This month / Last
 *    month / Next month / Custom range.
 *  - Admin click-to-drill: click a staff name to scope the view (and
 *    CSV export) to that one person; "Show all" reverts.
 *  - Self mode is auto-scoped to the current user (no name picker).
 *  - Two layouts:
 *      • Weekly grid — when the range is exactly one Mon–Sun week
 *        (default behaviour, preserved for the approval workflow).
 *      • Compact date table — when the range is anything else, so the
 *        page stays scannable for monthly/custom views.
 *  - Submit / Approve buttons are tied to the standard weekly approval
 *    contract and only show on whole-week ranges; a small banner
 *    explains this for other ranges.
 *  - CSV export respects both the active date range and the focused
 *    staff_id (admin only).
 *
 * Props:
 *  - mode: 'admin' | 'self'
 *  - title (optional): override the heading text.
 */

// Date helpers --------------------------------------------------------------
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const startOfWeek = (d) => {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // make Mon=0
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - day);
  return x;
};

const addDays = (d, n) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() + n);
  return x;
};

const startOfMonth = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(1);
  return x;
};

const endOfMonth = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setMonth(x.getMonth() + 1, 0); // day 0 of next month → last day this month
  return x;
};

const addMonths = (d, n) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(1); // avoid month-overflow when day > 28
  x.setMonth(x.getMonth() + n);
  return x;
};

const ymd = (d) => {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parseYmd = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 0, 0, 0, 0);
};

const fmtRange = (start, end) =>
  `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString(
    undefined,
    { month: 'short', day: 'numeric', year: 'numeric' }
  )}`;

const minsToHrs = (m) => (m / 60).toFixed(2);

const daysBetween = (a, b) => {
  // inclusive days between two midnight-anchored Dates
  const ms = b.getTime() - a.getTime();
  return Math.round(ms / (24 * 60 * 60 * 1000)) + 1;
};

const isExactWeek = (start, end) => {
  // Mon–Sun, exactly 7 days, starts on Monday.
  if (!start || !end) return false;
  if (daysBetween(start, end) !== 7) return false;
  return start.getDay() === 1; // Mon
};

// Period chip definitions
const PERIOD_DEFS = [
  { id: 'this_week',  label: 'This week',  group: 'week'   },
  { id: 'last_week',  label: 'Last week',  group: 'week'   },
  { id: 'this_month', label: 'This month', group: 'month'  },
  { id: 'last_month', label: 'Last month', group: 'month'  },
  { id: 'next_month', label: 'Next month', group: 'month'  },
  { id: 'custom',     label: 'Custom',     group: 'custom' },
];

const computeRange = (period, customStart, customEnd) => {
  const today = new Date();
  switch (period) {
    case 'this_week': {
      const s = startOfWeek(today);
      return { start: s, end: addDays(s, 6) };
    }
    case 'last_week': {
      const s = addDays(startOfWeek(today), -7);
      return { start: s, end: addDays(s, 6) };
    }
    case 'this_month': return { start: startOfMonth(today), end: endOfMonth(today) };
    case 'last_month': {
      const ref = addMonths(today, -1);
      return { start: startOfMonth(ref), end: endOfMonth(ref) };
    }
    case 'next_month': {
      const ref = addMonths(today, 1);
      return { start: startOfMonth(ref), end: endOfMonth(ref) };
    }
    case 'custom': {
      const s = parseYmd(customStart);
      const e = parseYmd(customEnd);
      if (s && e && e >= s) return { start: s, end: e };
      // Fallback to current week while admin is filling the inputs.
      const w = startOfWeek(today);
      return { start: w, end: addDays(w, 6) };
    }
    default: {
      const w = startOfWeek(today);
      return { start: w, end: addDays(w, 6) };
    }
  }
};

// Flag pills -----------------------------------------------------------------
const FLAG_LABELS = {
  absent: { text: 'Absent', color: 'hsl(0 70% 55%)' },
  late_arrival: { text: 'Late', color: 'hsl(35 95% 50%)' },
  early_departure: { text: 'Early out', color: 'hsl(35 95% 50%)' },
  overtime: { text: 'Overtime', color: 'hsl(220 80% 55%)' },
  unscheduled: { text: 'Unscheduled', color: 'hsl(260 60% 55%)' },
  missing_clock_out: { text: 'Missed C-out', color: 'hsl(0 70% 55%)' },
  clock_out_without_in: { text: 'C-out only', color: 'hsl(0 70% 55%)' },
  double_clock_in: { text: 'Double C-in', color: 'hsl(35 95% 50%)' },
  on_leave: { text: 'On leave', color: 'hsl(190 65% 45%)' },
  leave_holiday: { text: '🌴 Holiday', color: 'hsl(190 65% 45%)' },
  leave_sick: { text: '🤒 Sick', color: 'hsl(0 70% 55%)' },
  leave_unpaid: { text: '🚫 Unpaid', color: 'hsl(35 95% 50%)' },
  leave_other: { text: '📋 Leave', color: 'hsl(260 55% 55%)' },
};

function FlagPill({ flag }) {
  const meta = FLAG_LABELS[flag] || { text: flag, color: 'hsl(var(--muted-foreground))' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '0.1rem 0.4rem',
        borderRadius: '999px',
        background: `${meta.color}15`,
        color: meta.color,
        fontSize: '0.65rem',
        fontWeight: 600,
        marginRight: '0.2rem',
        marginTop: '0.15rem',
        whiteSpace: 'nowrap',
      }}
      data-testid={`ts-flag-${flag}`}
    >
      {meta.text}
    </span>
  );
}

// Main component =============================================================
export function TimesheetView({ mode = 'admin', title }) {
  const [period, setPeriod] = useState('this_week');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const { start: rangeStart, end: rangeEnd } = useMemo(
    () => computeRange(period, customStart, customEnd),
    [period, customStart, customEnd]
  );
  const oneWeek = isExactWeek(rangeStart, rangeEnd);
  const numDays = daysBetween(rangeStart, rangeEnd);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [includeVolunteersInExport, setIncludeVolunteersInExport] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Admin-only: when set, the view is scoped to one staff member.
  const [focusedStaffId, setFocusedStaffId] = useState(null);
  const [editorState, setEditorState] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { start_date: ymd(rangeStart), end_date: ymd(rangeEnd) };
      if (mode === 'admin' && focusedStaffId) params.staff_id = focusedStaffId;
      const fetcher = mode === 'self' ? api.getMyTimesheet : api.getTimesheets;
      const result = await fetcher(params);
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to load timesheet');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [rangeStart.getTime(), rangeEnd.getTime(), mode, focusedStaffId]);

  const handleExport = async () => {
    setExportingCsv(true);
    try {
      const params = { start_date: ymd(rangeStart), end_date: ymd(rangeEnd) };
      if (mode === 'admin' && focusedStaffId) params.staff_id = focusedStaffId;
      if (includeVolunteersInExport) params.include_volunteers = true;
      await api.downloadTimesheetsCsv(params);
    } catch (err) {
      alert(`CSV export failed: ${err.message}`);
    } finally {
      setExportingCsv(false);
    }
  };

  const handleSubmitForApproval = async () => {
    if (!oneWeek) return;
    if (!window.confirm('Submit this week’s timesheet for admin approval? Once approved it will be locked.')) return;
    setSubmitting(true);
    try {
      await api.submitTimesheet({
        start_date: ymd(rangeStart),
        end_date: ymd(rangeEnd),
        period_start: ymd(rangeStart),
        period_end: ymd(rangeEnd),
      });
      await load();
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveEntry = async (entry) => {
    if (!entry.approval?.id) return;
    const note = window.prompt('Optional approval note:', '');
    if (note === null) return;
    try {
      await api.approveTimesheet(entry.approval.id, note);
      await load();
    } catch (err) {
      alert(err.message);
    }
  };
  const handleRejectEntry = async (entry) => {
    if (!entry.approval?.id) return;
    const note = window.prompt('Reason for rejection (visible to staff):', '');
    if (note === null) return;
    try {
      await api.rejectTimesheet(entry.approval.id, note);
      await load();
    } catch (err) {
      alert(err.message);
    }
  };

  const isApprovalLockedForStaff = (entry) => entry?.approval?.status === 'approved';
  const refresh = () => load();

  const grand = data?.grand_totals || {};
  const entries = data?.entries || [];
  const focusedEntry = focusedStaffId
    ? entries.find((e) => e.staff_id === focusedStaffId) || null
    : null;
  const visibleEntries = focusedStaffId
    ? (focusedEntry ? [focusedEntry] : [])
    : entries;

  const headingDefault = mode === 'self' ? '🕒 My Timesheet' : '🕒 Team Timesheets';

  return (
    <div data-testid={`timesheet-view-${mode}`}>
      {/* Header / controls */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.85rem' }}>
        <div style={{ flex: '1 1 240px' }}>
          <h2 style={{ margin: 0 }}>{title || headingDefault}</h2>
          <div style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }} data-testid="ts-range-label">
            {oneWeek ? `Week of ${fmtRange(rangeStart, rangeEnd)}` : `${fmtRange(rangeStart, rangeEnd)} · ${numDays} days`}
            {focusedEntry && (
              <span style={{ marginLeft: 8 }}>
                · 👤 Viewing <strong>{focusedEntry.name}</strong>
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          {mode === 'self' && (
            <button
              className="btn btn-primary"
              onClick={handleSubmitForApproval}
              disabled={submitting || loading || !oneWeek || (data?.entries?.[0]?.approval?.status === 'approved')}
              data-testid="ts-submit-approval"
              title={oneWeek ? '' : 'Switch to a single Mon–Sun week to submit for approval.'}
            >
              {(() => {
                const st = data?.entries?.[0]?.approval?.status;
                if (!oneWeek) return '📨 Submit (week only)';
                if (st === 'approved') return '🔒 Approved';
                if (st === 'submitted') return '🔁 Resubmit';
                if (st === 'rejected') return '↺ Resubmit';
                return submitting ? 'Submitting…' : '📨 Submit for approval';
              })()}
            </button>
          )}
          {mode === 'admin' && (
            <>
              <button
                className="btn btn-ghost"
                disabled={!oneWeek}
                onClick={async () => {
                  if (!oneWeek) return;
                  if (!window.confirm('Submit timesheets for everyone in this org for the current week? Already-approved rows are skipped.')) return;
                  try {
                    const r = await api.triggerTimesheetAutoSubmit();
                    alert(`Done — ${r.submissions} submission(s) for ${r.organization}.`);
                    await load();
                  } catch (err) {
                    alert(err.message);
                  }
                }}
                data-testid="ts-auto-submit-now"
                title={oneWeek ? '' : 'Auto-submit operates on the current week only.'}
              >
                ⚙️ Run auto-submit now
              </button>
              <label
                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}
                title="Volunteers are excluded from payroll exports by default. Tick to include them in the CSV."
              >
                <input
                  type="checkbox"
                  checked={includeVolunteersInExport}
                  onChange={(e) => setIncludeVolunteersInExport(e.target.checked)}
                  data-testid="ts-include-volunteers"
                />
                Include volunteers
              </label>
              <button
                className="btn btn-primary"
                onClick={handleExport}
                disabled={exportingCsv || loading}
                data-testid="ts-export-csv"
              >
                {exportingCsv ? 'Exporting…' : '⬇️ Export CSV'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Period filter chips */}
      <div className="card" style={{ padding: '0.6rem 0.75rem', marginBottom: '0.85rem', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }} data-testid="ts-period-bar">
        <strong style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Period</strong>
        {PERIOD_DEFS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setPeriod(p.id)}
            className={period === p.id ? 'btn btn-primary' : 'btn btn-ghost'}
            data-testid={`ts-period-${p.id}`}
            style={{ fontSize: '0.78rem', padding: '0.25rem 0.6rem' }}
          >
            {p.label}
          </button>
        ))}
        {period === 'custom' && (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginLeft: 6 }} data-testid="ts-custom-range">
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              data-testid="ts-custom-start"
              aria-label="Custom range start"
              style={{ fontSize: '0.78rem' }}
            />
            <span style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>→</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              data-testid="ts-custom-end"
              aria-label="Custom range end"
              style={{ fontSize: '0.78rem' }}
            />
          </div>
        )}
        {!oneWeek && (
          <span style={{ fontSize: '0.72rem', color: 'hsl(35 80% 35%)', marginLeft: 'auto' }} data-testid="ts-week-only-hint">
            ℹ️ Submit/approval actions are limited to whole-week ranges.
          </span>
        )}
      </div>

      {/* Focused-staff banner (admin) */}
      {mode === 'admin' && focusedStaffId && (
        <div
          className="card"
          style={{ padding: '0.6rem 0.75rem', marginBottom: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6, background: 'hsl(var(--primary) / 0.06)' }}
          data-testid="ts-focus-banner"
        >
          <span style={{ fontSize: '0.85rem' }}>
            👤 Viewing only <strong>{focusedEntry?.name || 'selected staff'}</strong> for this period.
          </span>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setFocusedStaffId(null)}
            data-testid="ts-show-all"
            style={{ fontSize: '0.78rem', padding: '0.2rem 0.6rem' }}
          >
            ← Show all staff
          </button>
        </div>
      )}

      {/* Grand totals (admin only and only when viewing all staff) */}
      {mode === 'admin' && !focusedStaffId && data && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '0.5rem',
            marginBottom: '1rem',
          }}
          data-testid="ts-grand-totals"
        >
          {[
            ['Headcount', grand.headcount ?? 0],
            ['Worked hours', minsToHrs(grand.worked_minutes ?? 0)],
            ['Scheduled hours', minsToHrs(grand.scheduled_minutes ?? 0)],
            ['Gross pay', `£${(grand.gross_pay ?? 0).toFixed(2)}`],
            ['Late days', grand.late_days ?? 0],
            ['Absent days', grand.absent_days ?? 0],
            ['Overtime days', grand.overtime_days ?? 0],
          ].map(([label, value]) => (
            <div
              key={label}
              className="card"
              style={{ padding: '0.75rem 1rem', margin: 0 }}
            >
              <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {label}
              </div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      {error && <div className="error-message" data-testid="ts-error">{error}</div>}
      {loading && !data && <div data-testid="ts-loading">Loading timesheet…</div>}

      {!loading && visibleEntries.length === 0 && (
        <div className="empty-state" data-testid="ts-empty">
          <p>No staff profiles or activity for this period.</p>
        </div>
      )}

      {/* Per-staff rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
        {visibleEntries.map((e) => {
          const canEdit = mode === 'admin' || !isApprovalLockedForStaff(e);
          const onPickEditor = (s) => setEditorState(s);
          const rowProps = {
            key: e.staff_id,
            entry: e,
            rangeStart,
            rangeEnd,
            mode,
            isFocused: !!focusedStaffId,
            isOneWeek: oneWeek,
            onClickStaffName: mode === 'admin' && !focusedStaffId
              ? () => setFocusedStaffId(e.staff_id)
              : null,
            onApprove: mode === 'admin' && oneWeek ? () => handleApproveEntry(e) : null,
            onReject: mode === 'admin' && oneWeek ? () => handleRejectEntry(e) : null,
            canEditEvents: canEdit,
            onEditEvent: (event, day) => onPickEditor({
              mode: 'edit', staffId: e.staff_id, staffName: e.name, date: day.date, event,
            }),
            onDeleteEvent: async (event) => {
              if (!window.confirm('Delete this clock event? It will be soft-deleted (admins can restore).')) return;
              try {
                await api.deleteClockEvent(event._id);
                refresh();
              } catch (err) {
                window.alert(err.message);
              }
            },
            onAddEvent: (day) => onPickEditor({
              mode: 'create', staffId: e.staff_id, staffName: e.name, date: day.date,
            }),
            onUndoApproval: mode === 'admin' && oneWeek && e.approval?.status === 'approved'
              ? async () => {
                if (!window.confirm(`Undo the approved timesheet for ${e.name}? They'll be able to amend it again.`)) return;
                try {
                  await api.deleteTimesheetApproval(e.approval._id);
                  refresh();
                } catch (err) {
                  window.alert(err.message);
                }
              }
              : null,
          };
          return oneWeek
            ? <StaffWeeklyRow {...rowProps} />
            : <StaffTableRow {...rowProps} />;
        })}
      </div>

      {/* Clock-event editor modal */}
      {editorState && (
        <ClockEventEditor
          state={editorState}
          onClose={() => setEditorState(null)}
          onSaved={() => { setEditorState(null); refresh(); }}
        />
      )}
    </div>
  );
}

// Shared header (works for both layouts) ------------------------------------
function StaffRowHeader({ entry, mode, isOneWeek, onClickStaffName, onApprove, onReject, onUndoApproval }) {
  const { name, email, employment_type, hourly_rate, totals, approval } = entry;
  const APPROVAL_TONES = {
    submitted: { bg: 'hsl(45 95% 92%)', fg: 'hsl(35 80% 30%)', label: '⏳ Awaiting approval' },
    approved: { bg: 'hsl(140 50% 92%)', fg: 'hsl(140 50% 30%)', label: '✅ Approved' },
    rejected: { bg: 'hsl(0 70% 94%)', fg: 'hsl(0 60% 35%)', label: '❌ Rejected' },
  };
  const apTone = approval ? APPROVAL_TONES[approval.status] : null;
  const NameTag = onClickStaffName ? 'button' : 'strong';
  const nameProps = onClickStaffName
    ? {
        type: 'button',
        onClick: onClickStaffName,
        style: {
          fontSize: '1rem', fontWeight: 700, background: 'transparent', border: 'none',
          padding: 0, cursor: 'pointer', color: 'hsl(var(--primary))', textDecoration: 'underline',
        },
        'data-testid': `ts-staff-name-${entry.staff_id}`,
        title: 'View only this staff member',
      }
    : { style: { fontSize: '1rem' } };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '0.75rem', justifyContent: 'space-between' }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <NameTag {...nameProps}>{name}</NameTag>
          <span
            className="badge"
            style={{
              background: employment_type === 'volunteer'  ? 'hsl(140 50% 90%)'
                       : employment_type === 'contractor' ? 'hsl(45 95% 90%)'
                       : 'hsl(190 65% 92%)',
              color: employment_type === 'volunteer'  ? 'hsl(140 55% 28%)'
                  : employment_type === 'contractor' ? 'hsl(35 80% 30%)'
                  : 'hsl(190 65% 25%)',
              fontSize: '0.7rem',
              padding: '0.15rem 0.45rem',
              borderRadius: '999px',
            }}
            data-testid={`ts-emp-type-${entry.staff_id}`}
          >
            {employment_type === 'volunteer'  ? '🤝 Volunteer'
             : employment_type === 'contractor' ? '🧑‍💼 Contractor'
             : '👨‍🏫 Employee'}
          </span>
          {apTone && (
            <span
              className="badge"
              style={{
                background: apTone.bg, color: apTone.fg,
                fontSize: '0.7rem', padding: '0.15rem 0.45rem',
                borderRadius: '999px', fontWeight: 600,
              }}
              data-testid={`ts-approval-${entry.staff_id}`}
            >
              {apTone.label}
            </span>
          )}
        </div>
        <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>
          {email}{employment_type === 'volunteer' ? ' · 🤝 unpaid' : ` · £${hourly_rate.toFixed(2)}/hr`}
        </div>
        {approval?.review_note && (
          <div style={{ fontSize: '0.78rem', marginTop: '0.2rem', color: 'hsl(var(--muted-foreground))' }}>
            📝 {approval.review_note}
          </div>
        )}
        {mode === 'admin' && isOneWeek && approval?.status === 'submitted' && (
          <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={onApprove} data-testid={`ts-approve-${entry.staff_id}`}>✅ Approve</button>
            <button className="btn btn-secondary" onClick={onReject} data-testid={`ts-reject-${entry.staff_id}`}>❌ Reject</button>
          </div>
        )}
        {mode === 'admin' && isOneWeek && onUndoApproval && (
          <div style={{ marginTop: '0.5rem' }}>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onUndoApproval}
              style={{ fontSize: '0.78rem', color: 'hsl(0 60% 45%)' }}
              data-testid={`ts-undo-approval-${entry.staff_id}`}
            >
              ↩️ Undo approval
            </button>
          </div>
        )}
        {mode === 'self' && approval?.status === 'approved' && (
          <div
            style={{
              marginTop: '0.5rem',
              padding: '0.4rem 0.6rem',
              background: 'hsl(140 50% 95%)',
              color: 'hsl(140 50% 25%)',
              border: '1px solid hsl(140 40% 70%)',
              borderRadius: 6,
              fontSize: '0.78rem',
            }}
            data-testid={`ts-locked-banner-${entry.staff_id}`}
          >
            🔒 This week is approved and can no longer be amended. Contact your administrator to undo.
          </div>
        )}
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
          {employment_type === 'volunteer' ? 'Pay' : 'Gross pay'}
        </div>
        <div style={{ fontSize: '1.25rem', fontWeight: 700 }} data-testid={`ts-pay-${entry.staff_id}`}>
          {employment_type === 'volunteer' ? '—' : `£${(totals.gross_pay || 0).toFixed(2)}`}
        </div>
        <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
          {minsToHrs(totals.worked_minutes)} h worked
          {totals.scheduled_minutes ? ` · ${minsToHrs(totals.scheduled_minutes)} h scheduled` : ''}
        </div>
      </div>
    </div>
  );
}

// Weekly grid layout (range == 7 days, Mon–Sun) -----------------------------
function StaffWeeklyRow(props) {
  const { entry, rangeStart, canEditEvents, onEditEvent, onDeleteEvent, onAddEvent } = props;
  const { days } = entry;

  return (
    <div className="card" data-testid={`ts-row-${entry.staff_id}`} style={{ padding: '1rem' }}>
      <StaffRowHeader {...props} />
      <div className="ts-day-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(110px, 1fr))',
        gap: '0.4rem',
        marginTop: '0.85rem',
        overflowX: 'auto',
      }}>
        {days.map((day, i) => {
          const dayDate = addDays(rangeStart, i);
          const isToday = ymd(dayDate) === ymd(new Date());
          const worked = day.worked_minutes;
          const sched = day.scheduled_minutes;
          const flags = day.flags || [];
          return (
            <div
              key={day.date}
              data-testid={`ts-day-${day.date}-${entry.staff_id}`}
              style={{
                padding: '0.45rem 0.5rem',
                background: isToday ? 'hsl(var(--primary) / 0.08)' : 'hsl(var(--muted))',
                border: isToday ? '1px solid hsl(var(--primary) / 0.4)' : '1px solid hsl(var(--border))',
                borderRadius: 'var(--radius)',
                minWidth: 0,
              }}
            >
              <div style={{ fontSize: '0.7rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', justifyContent: 'space-between' }}>
                <span>{DAY_LABELS[i]}</span>
                <span>{dayDate.getDate()}</span>
              </div>
              <div style={{ fontSize: '0.95rem', fontWeight: 600 }}>
                {worked ? `${minsToHrs(worked)} h` : '—'}
              </div>
              {sched ? (
                <div style={{ fontSize: '0.7rem', color: 'hsl(var(--muted-foreground))' }}>
                  / {minsToHrs(sched)} sched
                </div>
              ) : null}
              {day.first_in && (
                <div style={{ fontSize: '0.7rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.15rem' }}>
                  {fmtTime(day.first_in)}
                  {day.last_out ? `–${fmtTime(day.last_out)}` : ''}
                </div>
              )}
              {flags.length > 0 && (
                <div style={{ marginTop: '0.2rem' }}>
                  {flags.map((f) => <FlagPill key={f} flag={f} />)}
                </div>
              )}
              {canEditEvents && Array.isArray(day.clock_events) && day.clock_events.length > 0 && (
                <div style={{ marginTop: '0.35rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }} data-testid={`ts-events-${day.date}-${entry.staff_id}`}>
                  {day.clock_events.map((ev) => (
                    <ClockEventLine
                      key={ev._id}
                      ev={ev}
                      onEdit={() => onEditEvent?.(ev, day)}
                      onDelete={() => onDeleteEvent?.(ev)}
                    />
                  ))}
                </div>
              )}
              {canEditEvents && (
                <button
                  type="button"
                  onClick={() => onAddEvent?.(day)}
                  style={{
                    marginTop: '0.3rem',
                    fontSize: '0.7rem',
                    border: '1px dashed hsl(var(--border))',
                    background: 'transparent',
                    width: '100%',
                    padding: '0.2rem',
                    borderRadius: 4,
                    cursor: 'pointer',
                    color: 'hsl(var(--muted-foreground))',
                  }}
                  data-testid={`ts-event-add-${day.date}-${entry.staff_id}`}
                >
                  + Add event
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Compact date-table layout (range != 7 days) -------------------------------
function StaffTableRow(props) {
  const { entry, canEditEvents, onEditEvent, onDeleteEvent, onAddEvent } = props;
  const { days } = entry;

  return (
    <div className="card" data-testid={`ts-row-${entry.staff_id}`} style={{ padding: '1rem' }}>
      <StaffRowHeader {...props} />
      <div style={{ overflowX: 'auto', marginTop: '0.85rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }} data-testid={`ts-table-${entry.staff_id}`}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'hsl(var(--muted-foreground))', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              <th style={{ padding: '0.3rem 0.4rem', borderBottom: '1px solid hsl(var(--border))' }}>Date</th>
              <th style={{ padding: '0.3rem 0.4rem', borderBottom: '1px solid hsl(var(--border))' }}>Day</th>
              <th style={{ padding: '0.3rem 0.4rem', borderBottom: '1px solid hsl(var(--border))' }}>Clock in</th>
              <th style={{ padding: '0.3rem 0.4rem', borderBottom: '1px solid hsl(var(--border))' }}>Clock out</th>
              <th style={{ padding: '0.3rem 0.4rem', borderBottom: '1px solid hsl(var(--border))', textAlign: 'right' }}>Worked</th>
              <th style={{ padding: '0.3rem 0.4rem', borderBottom: '1px solid hsl(var(--border))', textAlign: 'right' }}>Scheduled</th>
              <th style={{ padding: '0.3rem 0.4rem', borderBottom: '1px solid hsl(var(--border))' }}>Status</th>
              {canEditEvents && (
                <th style={{ padding: '0.3rem 0.4rem', borderBottom: '1px solid hsl(var(--border))' }}>Events</th>
              )}
            </tr>
          </thead>
          <tbody>
            {days.map((day) => {
              const isToday = day.date === ymd(new Date());
              const dayDate = parseYmd(day.date) || new Date(day.date);
              const dayName = dayDate.toLocaleDateString(undefined, { weekday: 'short' });
              return (
                <tr
                  key={day.date}
                  data-testid={`ts-day-${day.date}-${entry.staff_id}`}
                  style={{ background: isToday ? 'hsl(var(--primary) / 0.06)' : 'transparent' }}
                >
                  <td style={{ padding: '0.3rem 0.4rem', borderBottom: '1px solid hsl(var(--border) / 0.6)', fontWeight: 600 }}>{day.date}</td>
                  <td style={{ padding: '0.3rem 0.4rem', borderBottom: '1px solid hsl(var(--border) / 0.6)' }}>{dayName}</td>
                  <td style={{ padding: '0.3rem 0.4rem', borderBottom: '1px solid hsl(var(--border) / 0.6)' }}>
                    {day.first_in ? fmtTime(day.first_in) : '—'}
                  </td>
                  <td style={{ padding: '0.3rem 0.4rem', borderBottom: '1px solid hsl(var(--border) / 0.6)' }}>
                    {day.last_out ? fmtTime(day.last_out) : '—'}
                  </td>
                  <td style={{ padding: '0.3rem 0.4rem', borderBottom: '1px solid hsl(var(--border) / 0.6)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {day.worked_minutes ? `${minsToHrs(day.worked_minutes)} h` : '—'}
                  </td>
                  <td style={{ padding: '0.3rem 0.4rem', borderBottom: '1px solid hsl(var(--border) / 0.6)', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'hsl(var(--muted-foreground))' }}>
                    {day.scheduled_minutes ? `${minsToHrs(day.scheduled_minutes)} h` : '—'}
                  </td>
                  <td style={{ padding: '0.3rem 0.4rem', borderBottom: '1px solid hsl(var(--border) / 0.6)' }}>
                    {(day.flags || []).map((f) => <FlagPill key={f} flag={f} />)}
                  </td>
                  {canEditEvents && (
                    <td style={{ padding: '0.3rem 0.4rem', borderBottom: '1px solid hsl(var(--border) / 0.6)' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-testid={`ts-events-${day.date}-${entry.staff_id}`}>
                        {(day.clock_events || []).map((ev) => (
                          <ClockEventLine
                            key={ev._id}
                            ev={ev}
                            onEdit={() => onEditEvent?.(ev, day)}
                            onDelete={() => onDeleteEvent?.(ev)}
                            compact
                          />
                        ))}
                        <button
                          type="button"
                          onClick={() => onAddEvent?.(day)}
                          style={{
                            fontSize: '0.7rem',
                            border: '1px dashed hsl(var(--border))',
                            background: 'transparent',
                            padding: '0.1rem 0.3rem',
                            borderRadius: 4,
                            cursor: 'pointer',
                            color: 'hsl(var(--muted-foreground))',
                            alignSelf: 'flex-start',
                          }}
                          data-testid={`ts-event-add-${day.date}-${entry.staff_id}`}
                        >
                          + Add event
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Tiny shared component for clock-event lines -------------------------------
function ClockEventLine({ ev, onEdit, onDelete, compact = false }) {
  const tDisplay = fmtTime(ev.timestamp);
  const label = ev.event_type === 'clock_in' ? '⬇️'
    : ev.event_type === 'clock_out' ? '⬆️'
    : ev.event_type === 'break_start' ? '☕▶'
    : ev.event_type === 'break_end' ? '☕■' : '•';
  const editedTip = ev.edited_at
    ? `Edited ${fmtDateTime(ev.edited_at)}` + (ev.original_timestamp ? ` (was ${fmtTime(ev.original_timestamp)})` : '')
    : ev.manually_added ? 'Manually added' : '';
  return (
    <div
      title={editedTip}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.2rem',
        fontSize: compact ? '0.7rem' : '0.7rem',
        background: ev.edited_at ? 'hsl(45 95% 95%)' : 'transparent',
        border: ev.edited_at ? '1px dashed hsl(45 90% 60%)' : 'none',
        borderRadius: 4, padding: ev.edited_at ? '0.1rem 0.25rem' : 0,
      }}
      data-testid={`ts-event-${ev._id}`}
    >
      <span style={{ flex: 1 }}>
        {label} {tDisplay || '—'}
        {ev.edited_at && <span style={{ marginLeft: 4 }}>✏️</span>}
      </span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onEdit?.(); }}
        title="Edit"
        style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '0 2px' }}
        data-testid={`ts-event-edit-${ev._id}`}
      >✏️</button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDelete?.(); }}
        title="Delete"
        style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '0 2px', color: 'hsl(0 60% 50%)' }}
        data-testid={`ts-event-delete-${ev._id}`}
      >🗑️</button>
    </div>
  );
}

// ClockEventEditor (unchanged) ----------------------------------------------
function ClockEventEditor({ state, onClose, onSaved }) {
  const { mode, staffId, staffName, date, event } = state;
  const [eventType, setEventType] = useState(event?.event_type || 'clock_in');
  const [timestamp, setTimestamp] = useState(() => {
    if (event?.timestamp) return toDateTimeLocalInput(event.timestamp);
    return `${date}T09:00`;
  });
  const [notes, setNotes] = useState(event?.notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const isoTimestamp = dateTimeLocalToUtcIso(timestamp);
      if (!isoTimestamp) throw new Error('Invalid date / time. Please pick a valid value.');
      const payload = {
        event_type: eventType,
        timestamp: isoTimestamp,
        notes: notes.trim() || undefined,
      };
      if (mode === 'edit') {
        await api.updateClockEvent(event._id, payload);
      } else {
        await api.adminCreateClockEvent({ staff_id: staffId, ...payload });
      }
      onSaved();
    } catch (err) {
      setError(err.message || 'Failed to save clock event');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
      onClick={onClose}
      data-testid="clock-event-editor-modal"
    >
      <div
        className="card"
        style={{ width: '90%', maxWidth: 480, padding: '1.5rem', margin: '1rem' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ marginTop: 0 }}>{mode === 'edit' ? '✏️ Edit clock event' : '➕ Add clock event'}</h3>
        <div style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))', marginBottom: '1rem' }}>
          {staffName} · {new Date(date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
        </div>
        {error && <div className="error-message" style={{ marginBottom: '1rem' }} data-testid="clock-event-editor-error">{error}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.3rem' }}>Event type</label>
            <select
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: 'var(--radius)', border: '1px solid hsl(var(--border))', background: 'hsl(var(--background))' }}
              data-testid="clock-event-editor-type"
            >
              <option value="clock_in">⬇️ Clock in</option>
              <option value="clock_out">⬆️ Clock out</option>
              <option value="break_start">☕▶ Break start</option>
              <option value="break_end">☕■ Break end</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.3rem' }}>Date & time</label>
            <input
              type="datetime-local"
              value={timestamp}
              onChange={(e) => setTimestamp(e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: 'var(--radius)', border: '1px solid hsl(var(--border))', background: 'hsl(var(--background))' }}
              data-testid="clock-event-editor-timestamp"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.3rem' }}>Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reason for edit or additional context..."
              rows={3}
              style={{ width: '100%', padding: '0.5rem', borderRadius: 'var(--radius)', border: '1px solid hsl(var(--border))', background: 'hsl(var(--background))', resize: 'vertical' }}
              data-testid="clock-event-editor-notes"
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving} data-testid="clock-event-editor-cancel">Cancel</button>
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving} data-testid="clock-event-editor-save">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TimesheetView;
